'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

import { useLocale } from '@/components/LocaleContext'
import IconChevronDown from '@/components/shared/icons/ChevronDown'
import { isLoggedInClient } from '@/lib/auth'
import type { Localization } from '@/payload-types'
import { SEARCH, SEARCH_TYPES } from '@/utilities/constant'
import { getLocalizedTranslationData } from '@/utilities/translationsData'
import { DynamicDimensionSearchBar } from './search/DimensionSearchDynamic'

function IconSearch({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  )
}

export function HeaderSearchBar() {
  const { locale, translatedData } = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [dropdownRect, setDropdownRect] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  const isLoggedIn = isLoggedInClient()
  const staticCommon = getLocalizedTranslationData((locale as AppLocale) ?? 'en').common
  const commonT = (key: keyof Localization['common'], enFallback: string) =>
    staticCommon?.[key] ??
    translatedData?.common?.[key] ??
    enFallback

  const searchTypeParam = searchParams.get('type') ?? SEARCH_TYPES[0]
  const searchTypeLabels = {
    smart: commonT('smart_search', SEARCH.SMART),
    cross: commonT('cross_reference', SEARCH.CROSS),
    dimension: commonT('dimension_search', SEARCH.DIMENSION),
  }
  const searchTypeOptions: { value: string; label: string }[] = [
    { value: SEARCH_TYPES[0], label: searchTypeLabels.smart },
    { value: SEARCH_TYPES[1], label: searchTypeLabels.cross },
  ]
  if (isLoggedIn) {
    searchTypeOptions.push({ value: SEARCH_TYPES[2], label: searchTypeLabels.dimension })
  }

  const normalizeSearchType = (type: string) => {
    const found = searchTypeOptions.find(
      (opt) => opt.value === type || opt.label.toLowerCase() === type.toLowerCase(),
    )
    return found?.value ?? SEARCH_TYPES[0]
  }
  const searchType = normalizeSearchType(searchTypeParam)
  const searchTypeLabel =
    searchTypeOptions.find((opt) => opt.value === searchType)?.label ?? searchType

  const [keyword, setKeyword] = useState(() => searchParams.get('q') ?? '')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    setKeyword(searchParams.get('q') ?? '')
  }, [searchParams])

  useEffect(() => {
    if (dropdownOpen && triggerRef.current && typeof document !== 'undefined') {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 180) })
    } else {
      setDropdownRect(null)
    }
  }, [dropdownOpen])

  const handleSearch = () => {
    if (!keyword.trim() || keyword.trim().length < 4) {
      toast.warning(
        commonT('search_length_validation', 'Please enter at least 4 characters for search.'),
      )
      return
    }
    const params = new URLSearchParams()
    if (keyword.trim()) params.set('q', keyword.trim())
    params.set('type', searchType)
    router.push(`/products/search${params.toString() ? `?${params.toString()}` : ''}`, {
      scroll: false,
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSearch()
  }

  const dropdownContent =
    dropdownOpen && dropdownRect && typeof document !== 'undefined'
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              aria-hidden
              onClick={() => setDropdownOpen(false)}
            />
            <ul
              role="listbox"
              className="fixed z-[101] list-none rounded border border-neutral-200 bg-white py-1 shadow-lg font-poppins"
              style={{
                top: dropdownRect.top,
                left: dropdownRect.left,
                width: dropdownRect.width,
                minWidth: 180,
              }}
            >
              {searchTypeOptions.map((opt) => (
                <li key={opt.value} role="option" aria-selected={searchType === opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false)
                      const p = new URLSearchParams()
                      p.set('type', opt.value)
                      if (opt.value != SEARCH_TYPES[2]) {
                        const trimmed = keyword.trim()
                        if (trimmed) p.set('q', trimmed)
                      }
                      router.push(`/products/search?${p.toString()}`, { scroll: true })
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-[#111] hover:bg-neutral-100"
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </>,
          document.body,
        )
      : null

  return searchType !== SEARCH_TYPES[2] ? (
    <section
      className="w-full bg-neutral-100 py-4 sm:py-5"
      aria-label={commonT('search', 'Search')}
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col overflow-hidden rounded-md bg-white shadow-md sm:flex-row sm:items-stretch"
        >
          {/* Search Type */}
          <div className="flex flex-1 flex-col border-b border-neutral-200 py-3 pl-4 pr-3 sm:border-b-0 sm:border-r sm:py-4 sm:pr-4">
            <label className="font-poppins mb-1.5 text-xs font-medium uppercase tracking-wider text-neutral-500">
              {commonT('search_type', 'Search type')}
            </label>
            <div className="relative">
              <button
                ref={triggerRef}
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-2 border-0 bg-transparent py-1 text-left font-poppins text-base font-semibold text-[#111] focus:outline-none"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                aria-label={commonT('search_type', 'Search type')}
              >
                <span>{searchTypeLabel}</span>
                <IconChevronDown className="h-3 w-3 shrink-0 text-neutral-500" />
              </button>
              {dropdownContent}
            </div>
          </div>

          {/* Search Keyword */}
          <div className="flex flex-1 flex-col py-3 pl-4 pr-3 sm:py-4 sm:pl-5 sm:pr-4">
            <label
              htmlFor="header-search-keyword"
              className="font-poppins mb-1.5 text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              {commonT('search_prompt', 'Search keyword')}
            </label>
            <input
              id="header-search-keyword"
              name="q"
              type="search"
              value={keyword}
              onKeyDown={(e) => {
                if (e.code === 'Enter') handleSearch()
              }}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={commonT('search_placeholder', 'Enter part number')}
              className="w-full border-0 bg-transparent p-0 font-poppins text-base text-[#111] placeholder:text-neutral-400 focus:outline-none focus:ring-0"
              aria-label={commonT('search_prompt', 'Search keyword')}
            />
          </div>

          {/* Search button */}
          <div className="shrink-0 border-t border-neutral-200 p-3 sm:border-t-0 sm:border-l sm:px-4 sm:py-4">
            <button
              type="submit"
              className="flex w-full items-center justify-center gap-2 bg-[#FF0000] px-6 py-3 font-poppins text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#cc0000] focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:ring-offset-2 sm:w-auto disabled:cursor-not-allowed"
              aria-label={commonT('search', 'Search')}
              disabled={!keyword.trim()}
            >
              {commonT('search', 'Search')}
              <IconSearch className="h-5 w-5 shrink-0" />
            </button>
          </div>
        </form>
      </div>
    </section>
  ) : (
    <DynamicDimensionSearchBar />
  )
}
